import { Dayjs } from "dayjs";
import { useCallback, useMemo } from "react";
import { useRecoilValue } from "recoil";

import {
  MAIN_DATE_FORMAT,
  MAIN_TIME_FORMAT,
  NO_VALUE_DISPLAY,
  ZERO_TIME_HHmm,
} from "../constants";
import { ReportPeriodGroupingEnum } from "../enums";
import {
  caseHelper,
  dateISOApplyTimezone,
  formatDate,
  formatDayjsToTimeHHmm,
  formatTimeISO,
  formatTimeISOWithSeconds,
  getFormattedTimeFromTimeString,
  getISOWeekAndYear,
  isNullOrEmptySpace,
  Log,
  nowInTimezone,
  numericValueWithCommas,
} from "../helpers";
import { TTimeHHmm } from "../models";
import { profileDtoAtom, timezoneSelector } from "../recoil";

export type FormatDateTimeNormalizedFn = (
  dt: TDateISO | null | undefined,
  fallbackValue?: string | null | undefined,
) => string;

/** Appends `:ss` before an optional trailing AM/PM token (e.g. `hh:mm A` → `hh:mm:ss A`). */
const timeFormatWithSecondsMask = (timeFormat: string): string => {
  const trimmed = timeFormat.trimEnd();
  const ampmMatch = trimmed.match(/(\s+[Aa])$/);
  if (ampmMatch?.index !== undefined) {
    const withoutAmpm = trimmed.slice(0, ampmMatch.index).trimEnd();
    return `${withoutAmpm}:ss${ampmMatch[1]}`;
  }

  return `${trimmed}:ss`;
};

export const useFormats = () => {
  const profileDto = useRecoilValue(profileDtoAtom);
  const timezone = useRecoilValue(timezoneSelector);
  const dateFormat = useMemo(
    () => profileDto?.metadata?.dateFormat || MAIN_DATE_FORMAT,
    [profileDto?.metadata?.dateFormat],
  );
  const timeFormat = useMemo(
    () => profileDto?.metadata?.timeFormat || MAIN_TIME_FORMAT,
    [profileDto?.metadata?.timeFormat],
  );

  const dateTimeFormat = useMemo(
    () => `${dateFormat} ${timeFormat}`,
    [dateFormat, timeFormat],
  );
  const dateTimeFormatWithSeconds = useMemo(
    () => `${dateFormat} ${timeFormatWithSecondsMask(timeFormat)}`,
    [dateFormat, timeFormat],
  );
  const dateTimeDelimitedFormat = useMemo(
    () => `${dateFormat} - ${timeFormat}`,
    [dateFormat, timeFormat],
  );

  const isAMPM = useMemo(
    () => timeFormat.toLowerCase().includes("a"),
    [timeFormat],
  );

  const formatDayOfWeekTranslated = useCallback(
    (
      lang: string,
      options: Intl.DateTimeFormatOptions,
      letterCase?: "lower" | "start" | "upper",
      dateISOIfNotToday?: TDateISO,
    ): string => {
      if (!dateISOIfNotToday) {
        return caseHelper(
          nowInTimezone(timezone)
            .toDate()
            .toLocaleDateString(lang, { ...options, timeZone: timezone }),
          letterCase,
        );
      }

      return caseHelper(
        dateISOApplyTimezone(dateISOIfNotToday, timezone)
          .toDate()
          .toLocaleDateString(lang, { ...options, timeZone: timezone }),
        letterCase,
      );
    },
    [timezone],
  );
  const formatDateNormalized = useCallback(
    (dateISO: TDateISO | null | undefined): string => {
      if (!dateISO) {
        return NO_VALUE_DISPLAY;
      }

      return formatDate({ dateISO: dateISO, dateFormat, timezone });
    },
    [dateFormat, timezone],
  );

  const formatTimeNormalized = useCallback(
    (dateISO: TDateISO | null | undefined): string => {
      if (!dateISO) {
        return NO_VALUE_DISPLAY;
      }

      return formatDate({ dateISO: dateISO, dateFormat: timeFormat, timezone });
    },
    [timeFormat, timezone],
  );

  const formatDateTimeNormalized = useCallback<FormatDateTimeNormalizedFn>(
    (dateIso, fallbackValue) => {
      if (!dateIso) {
        return fallbackValue ?? NO_VALUE_DISPLAY;
      }

      return formatDate({
        dateISO: dateIso,
        dateFormat: `${dateFormat} ${timeFormat}`,
        timezone,
      });
    },
    [dateFormat, timeFormat, timezone],
  );

  const formatDateTimeWithSecondsNormalized =
    useCallback<FormatDateTimeNormalizedFn>(
      (dateIso, fallbackValue) => {
        if (!dateIso) {
          return fallbackValue ?? NO_VALUE_DISPLAY;
        }

        return formatDate({
          dateISO: dateIso,
          dateFormat: dateTimeFormatWithSeconds,
          timezone,
        });
      },
      [dateTimeFormatWithSeconds, timezone],
    );
  const formatDateTimeDelimited = useCallback(
    (dateIso: TDateISO | null | undefined): string => {
      if (!dateIso) {
        return NO_VALUE_DISPLAY;
      }

      return formatDate({
        dateISO: dateIso,
        dateFormat: dateTimeDelimitedFormat,
        timezone,
      });
    },
    [dateTimeDelimitedFormat, timezone],
  );

  const formatTimeHHmmNormalized = useCallback(
    (dt: TDateISO | null | undefined): string => {
      if (!dt) {
        return NO_VALUE_DISPLAY;
      }

      return formatTimeISO({ dateTimeISO: dt, timeFormat, timezone });
    },
    [timeFormat, timezone],
  );

  const formatTimeWithSecondsNormalized = useCallback(
    (dt: TDateISO | null | undefined): string => {
      if (!dt) {
        return NO_VALUE_DISPLAY;
      }

      return formatTimeISOWithSeconds({ dateTimeISO: dt, timezone });
    },
    [timezone],
  );

  const formatDayJsToTimeHHmm = useCallback(
    (date: Dayjs): TTimeHHmm => {
      if (!date) {
        Log.error("formatDayJsToTimeHHmm: date is null or undefined");
        return ZERO_TIME_HHmm;
      }

      return formatDayjsToTimeHHmm(date, timezone);
    },
    [timezone],
  );

  const formatTimeMainFormatNormalized = useCallback(
    (timeString: string | null | undefined): string => {
      if (!timeString) {
        return NO_VALUE_DISPLAY;
      }

      return getFormattedTimeFromTimeString(timeString);
    },
    [],
  );

  const getFormattedEditUserInfo = useCallback(
    (editor: string, editTimeISO: TDateISO | undefined) => {
      if (!editTimeISO) {
        return NO_VALUE_DISPLAY;
      }

      return `${!isNullOrEmptySpace(editor) ? editor : NO_VALUE_DISPLAY} @ ${
        formatDateTimeNormalized(editTimeISO) ?? NO_VALUE_DISPLAY
      }`;
    },
    [formatDateTimeNormalized],
  );

  const getFormattedDateTimePrint = useCallback(
    (timeText: string, dateText: string, isRtl: boolean) => {
      return isRtl ? `${timeText} / ${dateText}` : `${dateText} / ${timeText}`;
    },
    [],
  );

  const getFormattedOtherRideData = useCallback(
    (
      seatText: string,
      phoneText: string,
      passengerText: string,
      isRtl: boolean,
    ) => {
      return !isRtl
        ? `${seatText} - ${phoneText} - ${passengerText}`
        : `${passengerText} - ${phoneText} - ${seatText}`;
    },
    [],
  );
  const getSubheaderComparisonTranslated = useCallback(
    (lastText: string, weekdayText: string, isRtl: boolean) => {
      if (!lastText || !weekdayText) {
        return NO_VALUE_DISPLAY;
      }

      return isRtl
        ? `${weekdayText} ${lastText}`
        : `${lastText} ${weekdayText}`;
    },
    [],
  );

  const getLocaleTimeFromUTCTimeForCSV = useCallback(
    (datetimeISO: TDateISO | undefined) => {
      if (!datetimeISO) {
        return NO_VALUE_DISPLAY;
      }

      return formatDateTimeNormalized(datetimeISO);
    },
    [formatDateTimeNormalized],
  );

  const getNumberWithCurrencyNormalized = useCallback(
    (num: string | number, currency: string) => {
      if (currency === "") {
        return num;
      }

      return `${currency}${num}`;
    },
    [],
  );

  const getNumberWithCommasNormalized = useCallback((num: number) => {
    return numericValueWithCommas(num);
  }, []);

  const formatDateByGroupingPeriod = useCallback(
    (
      dateISO: TDateISO,
      period: ReportPeriodGroupingEnum | undefined,
    ): string => {
      switch (period) {
        case ReportPeriodGroupingEnum.Daily:
          return formatDateNormalized(dateISO);

        case ReportPeriodGroupingEnum.Weekly: {
          const { week, year } = getISOWeekAndYear(dateISO, timezone);
          return `${year} (${week})`;
        }

        case ReportPeriodGroupingEnum.Monthly:
          return formatDate({
            dateISO: dateISO,
            dateFormat: "YYYY-MM",
            timezone,
          });

        case ReportPeriodGroupingEnum.EntirePeriod:
        default:
          return "";
      }
    },
    [formatDateNormalized, timezone],
  );

  return {
    formatDateNormalized,
    formatDateTimeNormalized,
    formatDateTimeWithSecondsNormalized,
    formatDayOfWeekTranslated,
    getFormattedOtherRideData,
    formatTimeNormalized,
    getSubheaderComparisonTranslated,
    formatTimeHHmmNormalized,
    formatTimeWithSecondsNormalized,
    formatTimeMainFormatNormalized,
    getNumberWithCurrencyNormalized,
    getFormattedEditUserInfo,
    getFormattedDateTimePrint,
    getLocaleTimeFromUTCTimeForCSV,
    getNumberWithCommasNormalized,
    dateFormat,
    timeFormat,
    dateTimeFormat,
    dateTimeDelimitedFormat,
    formatDateTimeDelimited,
    isAMPM,
    formatDateByGroupingPeriod,
    formatDayJsToTimeHHmm,
  };
};
